import { useState, useEffect, useMemo } from 'react';
import { pricingService, PricingPlan, PricingAddon } from '../services/pricing.service';

const TYPE_FAMILY = 'APP_DEPLOY';
const OS_TYPE = 'GENERIC';
const OS_FAMILY = 'SERVER';

export interface MarketplacePricingSelection {
  typeFamily: string;
  osType: string;
  osFamily: string;
  planCode: string;
  variantCode: string;
  addons: string[];
}

export interface UseMarketplacePricingReturn {
  plans: PricingPlan[];
  isLoadingPlans: boolean;
  selectedPlanCode: string | null;
  selectedVariantCode: string | null;
  addons: PricingAddon[];
  isLoadingAddons: boolean;
  selectedAddonCodes: string[];
  setSelectedPlanCode: (code: string) => void;
  setSelectedVariantCode: (code: string) => void;
  toggleAddon: (code: string) => void;
  selectedPlan: PricingPlan | null;
  selectedVariant: PricingPlan | null;
  basePlans: PricingPlan[];
  variantsForSelected: PricingPlan[];
  totalPriceMinor: number;
  totalPrice: number;
  pricingSelection: MarketplacePricingSelection | null;
  isReady: boolean;
}

// RAM aproximada por tamaño de instancia (sufijo del instanceType).
// Sirve para filtrar planes por memoria mínima sin depender del catálogo.
const RAM_GB_BY_SIZE: Record<string, number> = {
  nano: 0.5, micro: 1, small: 2, medium: 4,
  large: 8, xlarge: 16, '2xlarge': 32, '4xlarge': 64,
};

function instanceRamGb(instanceType?: string): number {
  if (!instanceType) return 0;
  const size = instanceType.split('.').pop() || '';
  // Desconocido => no filtrar (asumimos que es suficientemente grande).
  return RAM_GB_BY_SIZE[size] ?? 999;
}

export interface UseMarketplacePricingOptions {
  /** Oculta planes cuyo instanceType tenga menos de esta RAM (GB). Ej: SQL Server requiere 2. */
  minRamGb?: number;
}

export function useMarketplacePricing(opts?: UseMarketplacePricingOptions): UseMarketplacePricingReturn {
  const minRamGb = opts?.minRamGb ?? 0;
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null);
  const [selectedVariantCode, setSelectedVariantCode] = useState<string | null>(null);
  const [addons, setAddons] = useState<PricingAddon[]>([]);
  const [isLoadingAddons, setIsLoadingAddons] = useState(false);
  const [selectedAddonCodes, setSelectedAddonCodes] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingPlans(true);
    pricingService
      .getPlans(TYPE_FAMILY, OS_TYPE, OS_FAMILY)
      .then((all) => {
        if (cancelled) return;
        // Filtra planes por RAM mínima (p.ej. SQL Server requiere ≥2 GB).
        const loaded = minRamGb > 0
          ? all.filter((p) => instanceRamGb(p.providerConfiguration?.instanceType) >= minRamGb)
          : all;
        setPlans(loaded);

        const grouped = new Map<string, PricingPlan[]>();
        loaded.forEach((p) => {
          if (!grouped.has(p.planCode)) grouped.set(p.planCode, []);
          grouped.get(p.planCode)!.push(p);
        });

        const firstBase = Array.from(grouped.values())
          .map((variants) => variants.find((v) => v.variantCode === 'STANDARD') ?? variants[0])
          .sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999))[0];

        if (firstBase) {
          setSelectedPlanCode(firstBase.planCode);
          setSelectedVariantCode(firstBase.variantCode ?? 'STANDARD');
        }
      })
      .catch(console.error)
      .finally(() => { if (!cancelled) setIsLoadingPlans(false); });

    return () => { cancelled = true; };
  }, [minRamGb]);

  useEffect(() => {
    if (!selectedPlanCode) { setAddons([]); setSelectedAddonCodes([]); return; }
    let cancelled = false;
    setIsLoadingAddons(true);
    pricingService
      .getPlanAddons(TYPE_FAMILY, OS_TYPE, selectedPlanCode, OS_FAMILY)
      .then((loaded) => {
        if (cancelled) return;
        setAddons(loaded);
        setSelectedAddonCodes((prev) => prev.filter((c) => loaded.some((a) => a.addonCode === c)));
      })
      .catch(console.error)
      .finally(() => { if (!cancelled) setIsLoadingAddons(false); });

    return () => { cancelled = true; };
  }, [selectedPlanCode]);

  const handleSetPlan = (code: string) => {
    setSelectedPlanCode(code);
    const variants = plans.filter((p) => p.planCode === code);
    const std = variants.find((v) => v.variantCode === 'STANDARD') ?? variants[0];
    setSelectedVariantCode(std?.variantCode ?? 'STANDARD');
  };

  const toggleAddon = (code: string) => {
    setSelectedAddonCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const basePlans = useMemo(() => {
    const seen = new Set<string>();
    return plans
      .filter((p) => {
        if (seen.has(p.planCode)) return false;
        seen.add(p.planCode);
        return true;
      })
      .map((p) => plans.find((v) => v.planCode === p.planCode && v.variantCode === 'STANDARD') ?? p)
      .sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999));
  }, [plans]);

  const variantsForSelected = useMemo(
    () =>
      plans
        .filter((p) => p.planCode === selectedPlanCode)
        .sort((a, b) => (a.variantOrder ?? 9999) - (b.variantOrder ?? 9999)),
    [plans, selectedPlanCode]
  );

  const selectedVariant = useMemo(
    () => variantsForSelected.find((v) => v.variantCode === selectedVariantCode) ?? null,
    [variantsForSelected, selectedVariantCode]
  );

  const selectedPlan = selectedVariant;

  const addonTotal = useMemo(
    () =>
      addons
        .filter((a) => selectedAddonCodes.includes(a.addonCode))
        .reduce((sum, a) => sum + (a.priceAmountMinor ?? 0), 0),
    [addons, selectedAddonCodes]
  );

  const totalPriceMinor = (selectedVariant?.priceAmountMinor ?? 0) + addonTotal;
  const totalPrice = totalPriceMinor / 100;

  const pricingSelection: MarketplacePricingSelection | null = selectedPlanCode && selectedVariantCode
    ? {
        typeFamily: TYPE_FAMILY,
        osType: OS_TYPE,
        osFamily: OS_FAMILY,
        planCode: selectedPlanCode,
        variantCode: selectedVariantCode,
        addons: selectedAddonCodes,
      }
    : null;

  const isReady = !isLoadingPlans && !!selectedPlanCode && !!selectedVariantCode;

  return {
    plans,
    isLoadingPlans,
    selectedPlanCode,
    selectedVariantCode,
    addons,
    isLoadingAddons,
    selectedAddonCodes,
    setSelectedPlanCode: handleSetPlan,
    setSelectedVariantCode,
    toggleAddon,
    selectedPlan,
    selectedVariant,
    basePlans,
    variantsForSelected,
    totalPriceMinor,
    totalPrice,
    pricingSelection,
    isReady,
  };
}
